import React, { useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight, Trash2, Clock, Share2, Eye, Star } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import StoryViewsModal from "@/components/StoryViewsModal";

const APP_ORIGIN = "https://redras.app";

const buildShareUrl = (story) => {
  const target = story.entrepreneur_id || story.profile_id;
  return `${APP_ORIGIN}/entrepreneur/${target}?story=${story.id}`;
};

const buildShareText = (story, profileName) => {
  const url = buildShareUrl(story);
  const nombre = profileName || story.profile_name || "un emprendimiento";
  const line = story.caption
    ? `${story.caption}\n\n`
    : "";
  return `${line}Mira la historia de ${nombre} en RED de Amor y Solidaridad 👇\n${url}`;
};

/**
 * Full-screen story viewer (Instagram-style).
 *
 * Props:
 *   group      { profile_id, profile_name, profile_logo, stories: [...] }
 *   onClose()
 *   onDelete?(story_id)  — optional, shown when the current user owns the story
 *   canDelete  boolean
 */
const STORY_DURATION_MS = 15000; // 15s per slide

const timeLeft = (iso) => {
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return "Vencida";
  const h = Math.floor(diff / (1000 * 60 * 60));
  if (h >= 24) return `Vence en ${Math.floor(h / 24)} d`;
  if (h >= 1) return `Vence en ${h} h`;
  const m = Math.max(1, Math.floor(diff / (1000 * 60)));
  return `Vence en ${m} min`;
};

const StoryViewer = ({ group, onClose, onDelete, onPinChanged, canDelete = false }) => {
  const stories = group?.stories || [];
  const [idx, setIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [shareToast, setShareToast] = useState("");
  const [viewsOpen, setViewsOpen] = useState(false);
  const timerRef = useRef(null);
  const startedAtRef = useRef(Date.now());
  const pausedRef = useRef(false);
  const viewedRef = useRef(new Set()); // dedupe view POST per story per open
  const videoRef = useRef(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  const current = stories[idx];
  const isVideo = current?.media_type === "video";

  // Pause auto-advance while auxiliary modals are open (views list, delete confirm)
  useEffect(() => {
    pausedRef.current = viewsOpen;
    if (isVideo && videoRef.current) {
      if (viewsOpen) videoRef.current.pause();
      else videoRef.current.play().catch(() => {});
    }
  }, [viewsOpen, isVideo]);

  useEffect(() => {
    if (!current) return;
    if (viewedRef.current.has(current.id)) return;
    viewedRef.current.add(current.id);
    api.post(`/stories/${current.id}/view`).catch(() => {});
  }, [current?.id]);

  useEffect(() => {
    startedAtRef.current = Date.now();
    setProgress(0);
    if (timerRef.current) clearInterval(timerRef.current);
    // For video stories we drive the progress bar from the video's own
    // timeupdate event (see below). For images use a wall clock. For videos we
    // also arm a safety timer (30 s) so a stuck autoplay can't freeze the viewer.
    if (isVideo) {
      const safety = setInterval(() => {
        if (pausedRef.current) return;
        const elapsed = Date.now() - startedAtRef.current;
        if (elapsed > 30_000) {
          if (idx < stories.length - 1) setIdx((i) => i + 1);
          else onCloseRef.current?.();
        }
      }, 1000);
      timerRef.current = safety;
      return () => clearInterval(safety);
    }
    timerRef.current = setInterval(() => {
      if (pausedRef.current) return;
      const elapsed = Date.now() - startedAtRef.current;
      const pct = Math.min(100, (elapsed / STORY_DURATION_MS) * 100);
      setProgress(pct);
      if (pct >= 100) {
        if (idx < stories.length - 1) {
          setIdx((i) => i + 1);
        } else {
          onCloseRef.current?.();
        }
      }
    }, 50);
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [idx, stories.length, isVideo]);

  // Try to autoplay unmuted; if the browser blocks it (autoplay policy),
  // fall back to muted playback so the story doesn't get stuck.
  const attemptPlay = () => {
    const v = videoRef.current;
    if (!v) return;
    const p = v.play();
    if (p && typeof p.catch === "function") {
      p.catch(() => {
        v.muted = true;
        v.play().catch(() => {});
      });
    }
  };
  useEffect(() => {
    if (!isVideo) return;
    // Slight delay to let the src load
    const t = setTimeout(attemptPlay, 50);
    return () => clearTimeout(t);
  }, [isVideo, current?.id]);

  // Video-driven progress + auto-advance on ended
  const handleVideoTime = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    setProgress(Math.min(100, (v.currentTime / v.duration) * 100));
  };
  const handleVideoEnded = () => {
    if (idx < stories.length - 1) setIdx((i) => i + 1);
    else onCloseRef.current?.();
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") setIdx((i) => Math.max(0, i - 1));
      if (e.key === "ArrowRight") setIdx((i) => Math.min(stories.length - 1, i + 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [stories.length]);

  if (!current) return null;

  const handleTap = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    if (x < rect.width / 3) {
      setIdx((i) => Math.max(0, i - 1));
    } else if (x > (rect.width * 2) / 3) {
      if (idx < stories.length - 1) setIdx((i) => i + 1);
      else onClose();
    }
  };

  const handleDelete = async () => {
    if (!onDelete || !current) return;
    if (!window.confirm("¿Eliminar esta historia?")) return;
    await onDelete(current.id);
    // The parent will remove from the group; here just advance/close
    if (stories.length <= 1) {
      onClose();
    } else if (idx >= stories.length - 1) {
      setIdx((i) => Math.max(0, i - 1));
    }
  };

  const handleTogglePin = async () => {
    if (!current) return;
    pausedRef.current = true;
    try {
      const { data } = await api.post(`/stories/${current.id}/pin`);
      setShareToast(data.is_pinned ? "Marcada como destacada ⭐" : "Ya no está destacada");
      onPinChanged?.(current.id, data.is_pinned);
    } catch (e) {
      setShareToast(e?.response?.data?.detail || "No se pudo actualizar");
    } finally {
      setTimeout(() => setShareToast(""), 2500);
      pausedRef.current = false;
    }
  };

  const openWhatsApp = () => {
    if (!current) return;
    const text = encodeURIComponent(buildShareText(current, group?.profile_name));
    // Pause auto-advance while user picks the recipient
    pausedRef.current = true;
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener,noreferrer");
    setShareToast("Abriendo WhatsApp…");
    setTimeout(() => setShareToast(""), 2500);
  };

  const copyLink = async () => {
    if (!current) return;
    const url = buildShareUrl(current);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        ta.remove();
      }
      setShareToast("Enlace copiado");
    } catch {
      setShareToast("No se pudo copiar el enlace");
    }
    setTimeout(() => setShareToast(""), 2500);
  };

  const nativeShare = async () => {
    if (!current) return;
    const url = buildShareUrl(current);
    const text = buildShareText(current, group?.profile_name);
    if (navigator.share) {
      pausedRef.current = true;
      try {
        await navigator.share({
          title: group?.profile_name || "Historia",
          text,
          url,
        });
      } catch (err) {
        // Swallow: navigator.share throws AbortError when user closes the sheet.
        // Only log unexpected errors.
        if (err && err.name !== "AbortError") {
          console.error("share failed", err);
        }
      } finally {
        pausedRef.current = false;
      }
    } else {
      openWhatsApp();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black flex items-center justify-center"
      data-testid="story-viewer"
      onMouseDown={() => { if (!viewsOpen) pausedRef.current = true; }}
      onMouseUp={() => { if (!viewsOpen) pausedRef.current = false; }}
      onTouchStart={() => { if (!viewsOpen) pausedRef.current = true; }}
      onTouchEnd={() => { if (!viewsOpen) pausedRef.current = false; }}
    >
      <div className="relative w-full h-full md:h-[90vh] md:max-w-md md:rounded-2xl overflow-hidden bg-black flex flex-col">
        {/* Progress bars */}
        <div className="absolute top-0 left-0 right-0 z-20 flex gap-1 p-3">
          {stories.map((s, i) => (
            <div key={s.id || `p-${i}`} className="flex-1 h-1 bg-white/25 rounded-full overflow-hidden">
              <div
                className="h-full bg-white transition-[width] duration-75"
                style={{
                  width: i < idx ? "100%" : i === idx ? `${progress}%` : "0%",
                }}
              />
            </div>
          ))}
        </div>

        {/* Header */}
        <div className="absolute top-5 left-0 right-0 z-20 flex items-center gap-3 px-4 pt-2">
          <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white shrink-0 bg-teal-mist">
            {group?.profile_logo ? (
              <img src={group.profile_logo} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white font-display bg-teal">
                {(group?.profile_name || "?").slice(0, 1)}
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <Link
              to={`/entrepreneur/${current.entrepreneur_id || current.profile_id}`}
              onClick={onClose}
              className="text-white text-sm font-bold truncate block hover:underline"
              data-testid="story-profile-link"
            >
              {group?.profile_name || current.profile_name}
            </Link>
            <p className="text-white/70 text-[11px] inline-flex items-center gap-1">
              {current.is_pinned ? (
                <><Star size={10} className="text-yellow-300 fill-yellow-300" /> Destacada</>
              ) : (
                <><Clock size={10} /> {timeLeft(current.expires_at)}</>
              )}
            </p>
          </div>
          {canDelete && (
            <>
              <button
                type="button"
                onClick={handleTogglePin}
                className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${current.is_pinned ? "bg-yellow-400 text-white hover:bg-yellow-500" : "bg-white/15 text-white hover:bg-yellow-400"}`}
                aria-label={current.is_pinned ? "Quitar destacada" : "Destacar historia"}
                data-testid="story-pin-btn"
                title={current.is_pinned ? "Quitar destacada" : "Destacar (no expira, máx 3)"}
              >
                <Star size={15} className={current.is_pinned ? "fill-white" : ""} />
              </button>
              <button
                type="button"
                onClick={() => setViewsOpen(true)}
                className="w-9 h-9 rounded-full bg-white/15 hover:bg-orange text-white flex items-center justify-center relative"
                aria-label="Ver quién vio"
                data-testid="story-views-btn"
              >
                <Eye size={15} />
                {current.view_count > 0 && (
                  <span className="absolute -top-1 -right-1 bg-orange text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">
                    {current.view_count}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="w-9 h-9 rounded-full bg-white/15 hover:bg-red-500/80 text-white flex items-center justify-center"
                aria-label="Eliminar historia"
                data-testid="story-delete"
              >
                <Trash2 size={15} />
              </button>
            </>
          )}
          <button
            type="button"
            onClick={nativeShare}
            className="w-9 h-9 rounded-full bg-white/15 hover:bg-orange text-white flex items-center justify-center"
            aria-label="Compartir historia"
            data-testid="story-share"
          >
            <Share2 size={15} />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center"
            aria-label="Cerrar"
            data-testid="story-close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Image / Video (click zones for prev/next) */}
        <div
          className="flex-1 flex items-center justify-center relative select-none px-2"
          onClick={handleTap}
        >
          {isVideo ? (
            <video
              ref={videoRef}
              key={current.id}
              src={current.image_url}
              className="max-w-full max-h-full object-contain block"
              autoPlay
              playsInline
              muted={false}
              onTimeUpdate={handleVideoTime}
              onEnded={handleVideoEnded}
              onClick={(e) => e.stopPropagation()}
              controls={false}
              data-testid="story-video"
            />
          ) : (
            <img
              src={current.image_url}
              alt=""
              className="max-w-full max-h-full object-contain block"
              draggable={false}
              data-testid="story-image"
            />
          )}
          {current.caption && (
            <div
              className="absolute bottom-16 left-4 right-4 z-10 bg-black/55 backdrop-blur-sm text-white text-sm md:text-base rounded-2xl px-4 py-3 leading-snug pointer-events-none"
              data-testid="story-caption"
            >
              {current.caption}
            </div>
          )}
        </div>

        {/* Share bar (bottom) */}
        <div
          className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 bg-black/55 backdrop-blur-md rounded-full px-2 py-1.5 shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={openWhatsApp}
            className="inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1fb457] text-white text-xs font-bold uppercase tracking-wider px-4 py-2 transition-colors"
            data-testid="story-share-whatsapp"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden>
              <path d="M20.5 3.5A11 11 0 0 0 3.6 17l-1.6 4.6 4.7-1.5A11 11 0 1 0 20.5 3.5Zm-8.5 18a9.4 9.4 0 0 1-4.8-1.3l-.3-.2-2.8.9.9-2.7-.2-.3A9.4 9.4 0 1 1 12 21.5Zm5.4-6.8c-.3-.2-1.8-.9-2-1s-.5-.2-.7.2-.8 1-.9 1.2-.3.2-.6 0a7.6 7.6 0 0 1-2.2-1.4 8.3 8.3 0 0 1-1.5-1.9c-.2-.4 0-.6.1-.7l.4-.5c.1-.2.2-.3.3-.5s0-.4 0-.5-.7-1.7-1-2.3-.5-.5-.7-.5h-.6a1.1 1.1 0 0 0-.8.4A3.4 3.4 0 0 0 6 8.9a5.9 5.9 0 0 0 1.2 3.1 13.5 13.5 0 0 0 5.2 4.6c.7.3 1.3.5 1.7.6a4.1 4.1 0 0 0 1.9.1 3.1 3.1 0 0 0 2-1.4 2.5 2.5 0 0 0 .2-1.4c-.1-.1-.3-.2-.6-.4Z" />
            </svg>
            <span>WhatsApp</span>
          </button>
          <button
            type="button"
            onClick={copyLink}
            className="inline-flex items-center gap-1 rounded-full bg-white/15 hover:bg-white/25 text-white text-xs font-bold uppercase tracking-wider px-3 py-2 transition-colors"
            aria-label="Copiar enlace"
            data-testid="story-copy-link"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M10 13a5 5 0 0 0 7.5 0l3-3a5 5 0 0 0-7-7l-1.5 1.5" />
              <path d="M14 11a5 5 0 0 0-7.5 0l-3 3a5 5 0 0 0 7 7l1.5-1.5" />
            </svg>
            <span>Copiar</span>
          </button>
        </div>

        {/* Toast */}
        {shareToast && (
          <div
            className="absolute top-20 left-1/2 -translate-x-1/2 z-30 bg-white/95 text-teal-deep text-xs font-bold uppercase tracking-wider rounded-full px-4 py-2 shadow-lg"
            data-testid="story-toast"
          >
            {shareToast}
          </div>
        )}

        {/* Side nav buttons (desktop) */}
        {idx > 0 && (
          <button
            type="button"
            onClick={() => setIdx((i) => Math.max(0, i - 1))}
            className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/15 hover:bg-white/30 text-white items-center justify-center"
            aria-label="Anterior"
          >
            <ChevronLeft size={20} />
          </button>
        )}
        {idx < stories.length - 1 && (
          <button
            type="button"
            onClick={() => setIdx((i) => i + 1)}
            className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/15 hover:bg-white/30 text-white items-center justify-center"
            aria-label="Siguiente"
          >
            <ChevronRight size={20} />
          </button>
        )}
      </div>

      {viewsOpen && current && (
        <StoryViewsModal
          storyId={current.id}
          onClose={() => setViewsOpen(false)}
        />
      )}
    </div>
  );
};

export default StoryViewer;
